import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { AlertCircle } from 'lucide-react';

import {
  usePatient,
  useBootstrap,
  useOrders,
  useUpdatePatient,
  useUpdatePatientAddress,
} from '@/lib/api/hooks';
import { brand } from '@brand';
import { Button } from '@/design-system/primitives';
import { Icon } from '@/lib/Icon';
import { cn } from '@/lib/cn';
import { track } from '@/lib/analytics';
import { PageHeader } from '@/components/app-shell';
import type { Patient, UpdateAddressBody } from '@/types/api';
import type { UpdatePatientBody } from '@/lib/api/hooks';
import { AddressFields } from './components/AccountFields';
import { SubscriptionsSection } from './SubscriptionsSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { PlanSwitchOfferModal } from './PlanSwitchOfferModal';
import { ClinicianAvatar } from '@/features/clinic/components/ClinicianAvatar';
import { memberDiscountPercent } from '@/lib/membership';
import { useSupportEmail } from '@/lib/supportEmail';
import type { BootstrapMembership } from '@/types/bootstrap';

/**
 * AccountScreen — list-of-sections layout.
 *
 * Each section is a mono-caps eyebrow + a stack of label/value rows
 * (`bg-[#ECECEC]` label band, `bg-[#FAFAFA]` value band) capped by a
 * full-width `bg-slate` action button with the brand Arrow icon on the
 * right (`justify-between`).
 */
export function AccountScreen() {
  const navigate = useNavigate();
  const { data: patient, isLoading } = usePatient();
  const { data: bootstrap } = useBootstrap();
  const supportEmail = useSupportEmail();
  const { data: orders } = useOrders();
  const [editingAddresses, setEditingAddresses] = useState(false);
  // Renewal-nudge deep link: /app/account?planOffer=<nudge id> lands here
  // from the email/SMS (via a freshly minted magic link) and opens the
  // quarterly plan-switch offer. Closing the modal drops the param so a
  // refresh or a back-navigation doesn't re-pitch.
  const [searchParams, setSearchParams] = useSearchParams();
  const planOfferId = searchParams.get('planOffer');

  // `?focus=payment` — deep link from the home screen's failed-payment
  // task. Declared above the loading early-return so the hook order stays
  // stable, and re-run once `patient` lands since the section doesn't
  // exist during the skeleton render.
  const paymentRef = useRef<HTMLDivElement | null>(null);
  const subscriptionsRef = useRef<HTMLDivElement | null>(null);
  const focusSubscriptions = searchParams.get('focus') === 'subscriptions';
  const focusSubscriptionSection = useCallback(() => {
    subscriptionsRef.current?.scrollIntoView({ block: 'start' });
  }, []);
  const focusPayment = searchParams.get('focus') === 'payment';
  // The first scroll runs while Payment methods and Subscriptions are still
  // loading, so the page is too short to bring the card to the top. Scroll
  // again once Subscriptions has rendered below it.
  const focusPaymentSection = useCallback(() => {
    paymentRef.current?.scrollIntoView({ block: 'start' });
  }, []);
  // `?focus=profile` — the cart's blood-test "Complete your profile" link.
  const focusProfile = searchParams.get('focus') === 'profile';
  const [editingProfile, setEditingProfile] = useState(focusProfile);
  const profileRef = useRef<HTMLDivElement | null>(null);
  // Open once, then drop the param: a patient refetch after saving or
  // cancelling must not reopen the editor.
  const profileReady = !isLoading && Boolean(patient);
  useEffect(() => {
    if (!focusProfile || !profileReady) return;
    setEditingProfile(true);
    profileRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }, [focusProfile, profileReady, searchParams, setSearchParams]);
  useEffect(() => {
    if (!focusPayment) return;
    const el = paymentRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusPayment, isLoading, patient]);

  if (isLoading || !patient) {
    if (brand.stableSkeletons) {
      return (
        <div className="flex flex-col" aria-busy="true" aria-label="Loading">
          <PageHeader
            title="Account"
            description="Manage your profile, addresses, payment methods and subscriptions."
          />
          <div className="flex flex-col px-15 md:px-40 py-25 gap-30 max-w-[65rem] mx-auto w-full">
            <div className="h-[36rem] border border-line-subtle bg-raised animate-pulse" />
            <div className="h-[20rem] border border-line-subtle bg-raised animate-pulse" />
          </div>
        </div>
      );
    }
    return <div className="h-[20rem] bg-sunken animate-pulse" />;
  }

  // Pharmacy flagged the patient's address on an open order.
  const addressVerificationPending = (orders ?? []).some(
    (o) => typeof o.issue === 'string' && /address/i.test(o.issue),
  );

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Account"
        description={
          brand.accountClinician
            ? 'Manage your profile, addresses, payment methods and subscriptions, and find your care team here.'
            : 'Manage your profile, addresses, payment methods and subscriptions.'
        }
      />

      <div className="flex flex-col px-15 md:px-40 py-25 gap-30 max-w-[65rem] mx-auto w-full">
        {/* ---------- PROFILE ---------- */}
        <div id="profile" ref={profileRef} className="scroll-mt-30">
        <Section title="Profile">
          {editingProfile ? (
            <ProfileEditor patient={patient} onDone={() => setEditingProfile(false)} />
          ) : (
            <>
              <FieldRow label="Name" value={`${patient.first_name} ${patient.last_name}`} />
              <FieldRow label="Email" value={patient.email} />
              <FieldRow label="Phone" value={patient.phone ? displayPhone(patient.phone) : '—'} />
              <FieldRow
                label="Date of birth"
                value={
                  patient.date_of_birth
                    ? format(parseISO(patient.date_of_birth), 'MMM d, yyyy')
                    : '—'
                }
              />
              <TimezoneRow
                timezone={patient.timezone}
                addressState={patient.address?.state ?? null}
              />
              <FieldRow label="Sex" value={sexIsSet(patient.sex) ? displayValue(patient.sex) : '—'} />
              <ActionButton label="Edit info" onClick={() => setEditingProfile(true)} />
            </>
          )}
        </Section>
        </div>

        {/* ---------- ADDRESSES ---------- */}
        <Section
          title="Addresses"
          eyebrowAccessory={
            addressVerificationPending && (
              <span className="inline-flex items-center gap-5 px-10 py-4 bg-warning/15 text-warning font-mono uppercase text-12">
                <AlertCircle size={11} strokeWidth={1.75} />
                Verify
              </span>
            )
          }
        >
          {editingAddresses ? (
            <AddressEditor
              patient={patient}
              onDone={() => setEditingAddresses(false)}
            />
          ) : (
            <>
              <AddressRow label="Shipping" address={patient.address} />
              <AddressRow label="Billing" address={patient.address} />
              <ActionButton
                label="Edit addresses"
                onClick={() => setEditingAddresses(true)}
              />
            </>
          )}
        </Section>

        {/* ---------- CLINICIAN ---------- */}
        {/* Storefront builds leave this out: the EMR-assigned provider can
            be a house clinician (brand.accountClinician). */}
        {brand.accountClinician && (
          <Section title="Your clinician">
            <ClinicianRow
              name={bootstrap?.clinician?.display_name ?? null}
              title={bootstrap?.clinician?.title ?? null}
              specialty={bootstrap?.clinician?.specialty ?? null}
            />
            {brand.careTeamMessaging && (
              <ActionButton
                label="Send a message"
                onClick={() => navigate('/app/clinic?message=1')}
              />
            )}
          </Section>
        )}

        {/* ---------- SUPPORT ---------- */}
        <Section title="Support">
          <SupportRow
            email={supportEmail}
            hours={bootstrap?.help_contact?.hours ?? null}
          />
          <ActionButton
            label="Contact"
            onClick={() => {
              window.location.href = `mailto:${supportEmail}`;
            }}
          />
        </Section>

        {/* ---------- MEMBERSHIP (members only) ---------- */}
        {bootstrap?.membership && memberDiscountPercent(bootstrap) > 0 && (
          <MembershipSection
            membership={bootstrap.membership}
            supportEmail={supportEmail}
          />
        )}

        {/* Payment methods — saved cards: confirm default, switch, add
            (typed card or Apple Pay / Google Pay), remove.
            `?focus=payment` scrolls here — used by the home screen's
            failed-payment task when no dunning link slug was available,
            so the patient lands on the card form rather than the top of
            a long settings page. */}
        <div id="payment-methods" ref={paymentRef} className="scroll-mt-30">
          <PaymentMethodsSection />
        </div>

        <div id="subscriptions" ref={subscriptionsRef} className="scroll-mt-30">
          <SubscriptionsSection
            onReady={
              focusSubscriptions
                ? focusSubscriptionSection
                : focusPayment
                  ? focusPaymentSection
                  : undefined
            }
          />
        </div>
      </div>

      {planOfferId && (
        <PlanSwitchOfferModal
          nudgeId={planOfferId}
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('planOffer');
            next.delete('src');
            setSearchParams(next, { replace: true });
          }}
        />
      )}
    </div>
  );
}

/**
 * Curated US timezones. Covers the vast majority of patients in one
 * dropdown; the auto-sync flow will populate any others (e.g. Atlantic
 * time, Guam) silently from the browser. If a patient ever needs to set
 * a non-US zone manually, we can extend this list — for now keep the UI
 * compact since the auto-sync covers it.
 */
const TZ_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
];

/** The timezone most of a US state's population uses (shipping address →
 *  a suggested timezone). States split across zones take the larger side. */
const STATE_TIMEZONE: Record<string, string> = Object.fromEntries(
  (
    [
      ['America/New_York', 'CT DC DE FL GA IN KY MA MD ME MI NC NH NJ NY OH PA RI SC VA VT WV'],
      ['America/Chicago', 'AL AR IA IL KS LA MN MO MS ND NE OK SD TN TX WI'],
      ['America/Denver', 'CO ID MT NM UT WY'],
      ['America/Phoenix', 'AZ'],
      ['America/Los_Angeles', 'CA NV OR WA'],
      ['America/Anchorage', 'AK'],
      ['Pacific/Honolulu', 'HI'],
    ] as const
  ).flatMap(([zone, states]) => states.split(' ').map((st) => [st, zone])),
);

function labelForTimezone(tz: string | null): string {
  if (!tz) return 'Not set';
  const known = TZ_OPTIONS.find((o) => o.value === tz);
  return known ? known.label : tz;
}

function TimezoneRow({
  timezone,
  addressState = null,
}: {
  timezone: string | null;
  addressState?: string | null;
}) {
  const update = useUpdatePatient();
  const [editing, setEditing] = useState(false);
  // Storefront: offer the shipping address's timezone when it differs
  // (accounts can start on a default zone the patient never chose).
  const suggested = brand.storefrontAccount
    ? STATE_TIMEZONE[(addressState ?? '').trim().toUpperCase()]
    : undefined;
  const suggestion =
    suggested && suggested !== timezone && TZ_OPTIONS.some((o) => o.value === suggested)
      ? suggested
      : null;
  // Local form state so we can show the dropdown in-row without making the
  // PATCH on every change — only fire on the explicit "Save" click. Matches
  // the inline-pattern the address editor uses below.
  const [draft, setDraft] = useState<string>(
    timezone ?? TZ_OPTIONS[0].value,
  );

  useEffect(() => {
    setDraft(timezone ?? TZ_OPTIONS[0].value);
  }, [timezone]);

  if (!editing) {
    return (
      <div className="flex flex-col">
        <div className="font-mono uppercase text-12 text-[#7C7C7C] bg-[#ECECEC] px-15 py-10">
          Timezone
        </div>
        <div className="font-body text-15 text-black bg-[#FAFAFA] px-15 py-12 flex items-center justify-between gap-15">
          <span className={timezone ? '' : 'text-[#7C7C7C]'}>
            {labelForTimezone(timezone)}
          </span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className={cn(
              'font-mono uppercase text-12 text-[#7C7C7C] hover:text-black underline underline-offset-2',
              brand.storefrontAccount &&
                'min-h-44 -my-12 -mr-10 px-10 inline-flex items-center text-13 shrink-0',
            )}
          >
            {timezone ? 'Change' : 'Set timezone'}
          </button>
        </div>
        {suggestion && (
          <div className="bg-[#FAFAFA] border-t border-[#ECECEC] px-15 py-10 flex flex-wrap items-center justify-between gap-x-15 gap-y-6">
            <span className="font-body text-14 text-ink-secondary leading-130">
              Your shipping address is in {addressState?.toUpperCase()}. Use{' '}
              {labelForTimezone(suggestion)} time?
            </span>
            <button
              type="button"
              disabled={update.isPending}
              onClick={async () => {
                try {
                  await update.mutateAsync({ timezone: suggestion });
                } catch {
                  /* the row keeps the suggestion; Change still works */
                }
              }}
              className="min-h-44 px-15 inline-flex items-center border border-line bg-page font-body text-14 text-ink hover:bg-sunken disabled:opacity-50"
            >
              {update.isPending ? 'Saving…' : 'Use this timezone'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="font-mono uppercase text-12 text-[#7C7C7C] bg-[#ECECEC] px-15 py-10">
        Timezone
      </div>
      <div className="bg-[#FAFAFA] px-15 py-12 flex flex-col gap-10">
        <select
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-40 px-10 bg-white border border-[#ECECEC] text-black text-13 focus:outline-none"
        >
          {TZ_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {/* Preserve a non-US current value so picking "Save" doesn't
              silently downgrade an exotic IANA zone the auto-sync set. */}
          {timezone && !TZ_OPTIONS.some((o) => o.value === timezone) && (
            <option value={timezone}>{timezone}</option>
          )}
        </select>
        {update.error && (
          <div className="text-13 text-danger">
            {(update.error as { message?: string }).message ?? 'Update failed'}
          </div>
        )}
        <div className="flex items-center gap-10">
          <Button
            type="button"
            loading={update.isPending}
            onClick={async () => {
              try {
                await update.mutateAsync({ timezone: draft });
                track('timezone_updated');
                setEditing(false);
              } catch {
                /* error rendered above */
              }
            }}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(timezone ?? TZ_OPTIONS[0].value);
              setEditing(false);
            }}
            disabled={update.isPending}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Membership ----------

function MembershipSection({
  membership,
  supportEmail,
}: {
  membership: BootstrapMembership;
  supportEmail: string;
}) {
  const date = membership.renews_at
    ? format(parseISO(membership.renews_at), 'MMM d, yyyy')
    : null;
  const price = `$${Math.round(membership.price_cents / 100)}/year`;
  return (
    <Section title="Membership">
      <FieldRow
        label="Status"
        value={
          membership.cancel_at_period_end
            ? `Active · ends ${date ?? 'at the end of your term'}`
            : 'Active'
        }
      />
      <FieldRow
        label={membership.cancel_at_period_end ? 'Price' : 'Renews'}
        value={
          membership.cancel_at_period_end || !date ? price : `${date} · ${price}`
        }
      />
      <FieldRow
        label="Perks"
        value={
          <ul className="flex flex-col gap-5">
            <li>{membership.discount_percent}% off every renewal, new treatment and Shop order</li>
            <li>
              At-home blood test every {membership.lab_interval_months} months, included
            </li>
          </ul>
        }
      />
      <div className="bg-[#FAFAFA] px-15 py-12 font-body text-13 text-[#7C7C7C]">
        To cancel your membership,{' '}
        <a
          href={`mailto:${supportEmail}?subject=${encodeURIComponent(`Cancel my ${brand.membershipName}`)}`}
          className="text-black underline underline-offset-4"
        >
          contact support
        </a>
        . Your perks continue until the end of your term.
      </div>
    </Section>
  );
}

// ---------- Primitives ----------

function Section({
  title,
  eyebrowAccessory,
  children,
}: {
  title: string;
  eyebrowAccessory?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-10">
      <div className="flex items-center justify-between gap-10">
        <h2 className="font-mono uppercase text-13 text-[#7C7C7C]">{title}</h2>
        {eyebrowAccessory}
      </div>
      <div className="flex flex-col border border-line-subtle">{children}</div>
    </section>
  );
}

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col">
      <div className="font-mono uppercase text-12 text-[#7C7C7C] bg-[#ECECEC] px-15 py-10">
        {label}
      </div>
      <div className="font-body text-15 text-black bg-[#FAFAFA] px-15 py-12">
        {value}
      </div>
    </div>
  );
}

function AddressRow({
  label,
  address,
}: {
  label: string;
  address: UpdateAddressBody | null;
}) {
  return (
    <FieldRow
      label={label}
      value={
        address ? (
          <span className="whitespace-pre-line leading-150">
            {[
              address.line1,
              address.line2,
              `${address.city}, ${address.state} ${address.postal_code}`,
            ]
              .filter(Boolean)
              .join('\n')}
          </span>
        ) : (
          <span className="text-[#7C7C7C]">No address on file.</span>
        )
      }
    />
  );
}

function ClinicianRow({
  name,
  title,
  specialty,
}: {
  name: string | null;
  title: string | null;
  specialty: string | null;
}) {
  if (!name) {
    return (
      <FieldRow
        label="Clinician"
        value={<span className="text-[#7C7C7C]">Not yet assigned.</span>}
      />
    );
  }
  return (
    <div className="flex flex-col">
      <div className="font-mono uppercase text-12 text-[#7C7C7C] bg-[#ECECEC] px-15 py-10">
        Clinician
      </div>
      <div className="bg-[#FAFAFA] px-15 py-15 flex items-center gap-15">
        <ClinicianAvatar name={name} size={56} />
        <div className="flex-1 min-w-0 flex flex-col gap-5">
          <div className="type-display-sm leading-100 text-black">{name}</div>
          {(title || specialty) && (
            <div className="font-body text-13 text-[#7C7C7C]">
              {[title, specialty].filter(Boolean).join(' | ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SupportRow({
  email,
  hours,
}: {
  email: string;
  hours: string | null;
}) {
  return (
    <div className="flex flex-col">
      <div className="bg-[#FAFAFA] px-15 py-15 flex flex-col gap-5">
        <a
          href={`mailto:${email}`}
          className="font-body text-15 text-black hover:underline underline-offset-4"
        >
          {email}
        </a>
        {hours && <div className="font-body text-13 text-[#7C7C7C]">{hours}</div>}
      </div>
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full h-50 px-15 bg-slate text-black font-body text-15 flex items-center justify-between hover:bg-[#CCCCCC] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span>{label}</span>
      <span aria-hidden className="inline-flex w-8">
        <Icon name="Arrow" viewBox="0 0 10 12" />
      </span>
    </button>
  );
}

// ---------- Profile editor ----------

const SEX_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
] as const;

/** brand.prettyProfileValues: a 10-digit US number (optionally +1) as
 *  (949) 555-0100; anything else as recorded. */
function displayPhone(phone: string): string {
  if (!brand.prettyProfileValues) return phone;
  const digits = phone.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return phone;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

/** brand.prettyProfileValues: a recorded enum value ("male",
 *  "prefer_not_to_say") in sentence case. */
function displayValue(value: string | null | undefined): string {
  const raw = value ?? '';
  if (!brand.prettyProfileValues || !raw) return raw;
  const words = raw.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function sexIsSet(sex: string | null | undefined): boolean {
  return Boolean(sex) && sex !== 'unknown';
}

/** How a recorded name, email, date of birth or sex gets changed: a care
 *  team message, or (no messaging, the storefront build) an email to the
 *  patient's support address. */
function CareTeamNote() {
  if (brand.careTeamMessaging) {
    return (
      <Link to="/app/clinic?message=1" className="underline underline-offset-2">
        Message your care team to change this
      </Link>
    );
  }
  return <SupportEmailNote />;
}

function SupportEmailNote() {
  const supportEmail = useSupportEmail();
  return (
    <a href={`mailto:${supportEmail}`} className="underline underline-offset-2">
      Email {supportEmail} to change this
    </a>
  );
}

const careTeamNote = <CareTeamNote />;

/**
 * Phone is always editable. Date of birth and sex can be filled in only
 * while they're missing; a recorded value (and name, email) changes
 * through the care team because ID verification and prescriptions use it.
 */
export function ProfileEditor({
  patient,
  onDone,
}: {
  patient: Pick<Patient, 'first_name' | 'last_name' | 'email' | 'phone' | 'date_of_birth' | 'sex'>;
  onDone: () => void;
}) {
  const update = useUpdatePatient();
  const dobMissing = !patient.date_of_birth;
  const sexMissing = !sexIsSet(patient.sex);
  const [phone, setPhone] = useState(patient.phone ?? '');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (update.isPending) return;
    // A phone can be changed but not removed: reminders and the pharmacy use it.
    if (!phone.trim() && patient.phone) {
      setPhoneError('Enter a phone number. You can change it, but not remove it.');
      return;
    }
    setPhoneError(null);
    const body: UpdatePatientBody = {};
    if (phone.trim() && phone.trim() !== (patient.phone ?? '')) body.phone = phone.trim();
    if (dobMissing && dob) body.date_of_birth = dob;
    if (sexMissing && sex) body.sex = sex as UpdatePatientBody['sex'];
    try {
      if (Object.keys(body).length) {
        await update.mutateAsync(body);
        track('profile_updated', { fields: Object.keys(body).join(',') });
      }
      onDone();
    } catch {
      /* error rendered below */
    }
  }

  const inputClass =
    'h-40 px-10 bg-white border border-[#ECECEC] text-black text-15 focus:outline-none';
  return (
    <form onSubmit={onSave} className="flex flex-col gap-15 bg-[#FAFAFA] p-15">
      <div className="flex flex-col gap-4">
        <span className="font-mono uppercase text-12 text-[#7C7C7C]">Name</span>
        <span className="text-15 text-black">{`${patient.first_name} ${patient.last_name}`}</span>
        <span className="text-13 text-[#7C7C7C]">{careTeamNote}</span>
      </div>
      <div className="flex flex-col gap-4">
        <span className="font-mono uppercase text-12 text-[#7C7C7C]">Email</span>
        <span className="text-15 text-black">{patient.email}</span>
        <span className="text-13 text-[#7C7C7C]">{careTeamNote}</span>
      </div>
      <label className="flex flex-col gap-4">
        <span className="font-mono uppercase text-12 text-[#7C7C7C]">Phone</span>
        <input
          type="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          aria-invalid={phoneError ? true : undefined}
          className={inputClass}
        />
        {phoneError && <span className="text-13 text-danger">{phoneError}</span>}
      </label>
      {dobMissing ? (
        <label className="flex flex-col gap-4">
          <span className="font-mono uppercase text-12 text-[#7C7C7C]">Date of birth</span>
          <input
            type="date"
            autoComplete="bday"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            className={inputClass}
          />
        </label>
      ) : (
        <div className="flex flex-col gap-4">
          <span className="font-mono uppercase text-12 text-[#7C7C7C]">Date of birth</span>
          <span className="text-15 text-black">
            {format(parseISO(patient.date_of_birth as string), 'MMM d, yyyy')}
          </span>
          <span className="text-13 text-[#7C7C7C]">{careTeamNote}</span>
        </div>
      )}
      {sexMissing ? (
        <label className="flex flex-col gap-4">
          <span className="font-mono uppercase text-12 text-[#7C7C7C]">Sex assigned at birth</span>
          <select value={sex} onChange={(e) => setSex(e.target.value)} className={inputClass}>
            <option value="">Select</option>
            {SEX_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <div className="flex flex-col gap-4">
          <span className="font-mono uppercase text-12 text-[#7C7C7C]">Sex</span>
          <span className="text-15 text-black">{displayValue(patient.sex)}</span>
          <span className="text-13 text-[#7C7C7C]">{careTeamNote}</span>
        </div>
      )}
      {update.error && (
        <div role="alert" className="text-13 text-danger">
          {(update.error as { message?: string }).message ?? 'Update failed'}
        </div>
      )}
      <div className="flex items-center gap-10">
        <Button type="submit" loading={update.isPending}>
          Save profile
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} disabled={update.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------- Address editor (kept inline for the existing flow) ----------

function AddressEditor({
  patient,
  onDone,
}: {
  patient: { address: UpdateAddressBody | null };
  onDone: () => void;
}) {
  const update = useUpdatePatientAddress();
  const [form, setForm] = useState<UpdateAddressBody>(() => ({
    line1: patient.address?.line1 ?? '',
    line2: patient.address?.line2 ?? '',
    city: patient.address?.city ?? '',
    state: patient.address?.state ?? '',
    postal_code: patient.address?.postal_code ?? '',
    country: patient.address?.country ?? 'US',
  }));

  useEffect(() => {
    setForm({
      line1: patient.address?.line1 ?? '',
      line2: patient.address?.line2 ?? '',
      city: patient.address?.city ?? '',
      state: patient.address?.state ?? '',
      postal_code: patient.address?.postal_code ?? '',
      country: patient.address?.country ?? 'US',
    });
  }, [patient]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (update.isPending) return;
    try {
      await update.mutateAsync(form);
      track('address_updated');
      onDone();
    } catch {
      /* error rendered below */
    }
  }

  return (
    <form onSubmit={onSave} className="flex flex-col gap-15 bg-[#FAFAFA] p-15">
      <AddressFields value={form} onChange={setForm} />
      {update.error && (
        <div className="text-13 text-danger">
          {(update.error as { message?: string }).message ?? 'Update failed'}
        </div>
      )}
      <div className="flex items-center gap-10">
        <Button type="submit" loading={update.isPending}>
          Save address
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onDone}
          disabled={update.isPending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
